import { Button, Paper, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import FormRole from './FormRole';
import { zodResolver } from '@hookform/resolvers/zod';
import { schema } from '../validations';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Roles } from '../types';
import { ListingEntry } from 'graphql/listings/queries';
import { updateRoleParams } from '../services/mutations';

const useStyles = makeStyles((theme) => ({
  title: {
    overflowWrap: 'anywhere',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
  },
  container: { padding: theme.spacing(2) },
  btn: {
    '& .MuiButton-label': {
      gap: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: { width: '100%' },
    '@media (hover: none) and (pointer: coarse)': { minHeight: theme.spacing(5.5) },
  },
  btnText: {
    color: theme.palette.primary.main,
  },
}));

interface DetailsItemProps {
  selectedRole: Roles;
  listingMutations: ListingEntry[];
  listingQueries: ListingEntry[];
  handleUpdateRole: (data: updateRoleParams) => void;
}

const DetailsItem: FC<DetailsItemProps> = ({
  selectedRole,
  listingMutations,
  listingQueries,
  handleUpdateRole,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues: { description: selectedRole.description, permissions: selectedRole.permissions },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    reset({ description: selectedRole.description, permissions: selectedRole.permissions });
  }, [reset, selectedRole]);

  const onSubmit = (data) => {
    handleUpdateRole({ ...data, id: selectedRole._id });
  };

  return (
    <Paper className={classes.container}>
      <Typography variant="h6" className={classes.title}>
        {t('roles:roleTitle', { description: selectedRole.description })}
      </Typography>
      <FormRole
        control={control}
        errors={errors}
        listingMutations={listingMutations}
        listingQueries={listingQueries}
      />
      <Button
        variant="contained"
        disableElevation
        color="secondary"
        className={classes.btn}
        onClick={handleSubmit(onSubmit)}
        disabled={!isValid}
      >
        <FontAwesomeIcon icon="cloud" />
        <Typography variant="subtitle2" className={classes.btnText}>
          {t('roles:save')}
        </Typography>
      </Button>
    </Paper>
  );
};

export default DetailsItem;
